import type { AiCostTotals, AiUsageType } from "@nlc/shared-types";
import type { UseQueryResult } from "@tanstack/react-query";

import { useQuery } from "@tanstack/react-query";

import { aiCostsKeys } from "@/hooks/queryKeys";

import type { AiCostsBreakdownTab, AiCostsRange } from "../aiCostsSearch";

import {
  getAiCostsByAccount,
  getAiCostsByFeature,
  getAiCostsByModel,
  getAiCostsByUsageType,
} from "../api/aiCosts.api";

export type AiCostsRow = {
  href?: string;
  id: string;
  name: string;
} & AiCostTotals;

export interface AiCosts {
  rows: AiCostsRow[];
  totals: AiCostTotals;
}

const USAGE_TYPE_LABELS: Record<AiUsageType, string> = {
  image: "Images",
  megapixel: "Images (per megapixel)",
  tokens: "Text (tokens)",
};

const FETCH_COSTS: Record<
  AiCostsBreakdownTab,
  (range: AiCostsRange) => Promise<AiCosts>
> = {
  accounts: async (range) => {
    const { rows, totals } = await getAiCostsByAccount(range);

    return {
      rows: rows.map(({ accountId, accountName, ...costs }) => ({
        ...costs,
        href: accountId === null ? undefined : `/projects/${accountId}`,
        id: String(accountId ?? "unattributed"),
        name: accountName ?? "Unattributed",
      })),
      totals,
    };
  },
  features: async (range) => {
    const { rows, totals } = await getAiCostsByFeature(range);

    return {
      rows: rows.map(({ feature, ...costs }) => ({
        ...costs,
        id: feature,
        name: feature,
      })),
      totals,
    };
  },
  models: async (range) => {
    const { rows, totals } = await getAiCostsByModel(range);

    return {
      rows: rows.map(({ model, provider, ...costs }) => ({
        ...costs,
        id: `${provider}/${model}`,
        name: `${provider} / ${model}`,
      })),
      totals,
    };
  },
  "usage-type": async (range) => {
    const { rows, totals } = await getAiCostsByUsageType(range);

    return {
      rows: rows.map(({ usageType, ...costs }) => ({
        ...costs,
        id: usageType,
        name: USAGE_TYPE_LABELS[usageType],
      })),
      totals,
    };
  },
};

export function useAiCosts(
  tab: AiCostsBreakdownTab,
  range: AiCostsRange | null,
): UseQueryResult<AiCosts, Error> {
  return useQuery({
    enabled: range !== null,
    queryFn: () => FETCH_COSTS[tab](range as AiCostsRange),
    queryKey: aiCostsKeys.costs(
      tab,
      range?.from.toISOString(),
      range?.to.toISOString(),
    ),
  });
}
